"use client";

import React, { useState } from "react";
import Button from "@/app/components/button";
import Close from "@/app/components/svg/close";
import InputField from "@/app/components/input";
import User from "@/app/components/svg/user";
import PhoneDef from "@/app/components/svg/phone-def";
import EmailIcon from "@/app/components/svg/email";
import Checkbox from "@/app/components/checkbox";
import ArrowRight from "../svg/arrow-right";
import BackBtnGray from "../svg/back-btn-gray";

interface CubeCalculatorModalProps {
    isOpen: boolean;
    onClose: () => void;
    items: Array<{
        room: string;
        item: string;
        value: number;
    }>;
    totalSpace: number;
    lang?: string; 
}

const CubeCalculatorModal: React.FC<CubeCalculatorModalProps> = ({
    isOpen,
    onClose,
    items,
    totalSpace,
    lang
}) => {
    const [step, setStep] = useState<1 | 2>(1);
    const [formData, setFormData] = useState({
        fullName: "",
        phoneNumber: "",
        email: "",
        message: "",
        agreed: false,
    });
    const [errors, setErrors] = useState({
        fullName: false,
        phoneNumber: false,
        email: false,
        emailFormat: false,
        agreed: false,
    });

    if (!isOpen) return null;

    const filteredItems = items.filter((item) => item.value > 0);

    const validateEmail = (email: string) => {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return regex.test(email);
    };

    const handleCheckboxChange = (checked: boolean) => {
        setFormData(prev => ({
            ...prev,
            agreed: checked
        }));
    };

    const validateStep1 = () => {
        return filteredItems.length > 0;
    };

    const validateStep2 = () => {
        const isEmailValid = validateEmail(formData.email);
        
        const newErrors = {
            fullName: !formData.fullName.trim(),
            phoneNumber: !formData.phoneNumber.trim(),
            email: !formData.email.trim(),
            emailFormat: !isEmailValid,
            agreed: !formData.agreed,
        };

        setErrors(newErrors);
        return !Object.values(newErrors).some(error => error);
    };

    const handleContinue = async () => {
        if (step === 1 && validateStep1()) {
            setStep(2);
        } else if (step === 2 && validateStep2()) {
            try {
                const res = await fetch("/api/cube-counter", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        items: filteredItems,
                        totalSpace,
                        formData,
                    }),
                });

                const result = await res.json();
                if (result.success) {
                    onClose();
                } else {
                    alert("Error sending form. Please try again.");
                }
            } catch (err) {
                console.error("Error submitting form:", err);
                alert("Submission failed.");
            }
        }
    };


    const handleBack = () => {
        if (step === 2) {
            setStep(1);
        }
    };

    return (
        <>
            <div className="w-screen h-screen flex justify-center items-center">
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-[40px] max-w-[744px] w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-8 max-[576px]:p-4">
                            {/* Header */}
                            <div className="flex justify-between items-center mb-8">
                                <h2 className="text-[32px] font-bold text-violet-500 max-[576px]:text-[24px]">
                                    {step === 1
                                        ? lang === 'en' ? "Your items summary" : "Beräknat resultat"
                                        : lang === 'en' ? "Fill in your data to get a price quote" : "Fyll i dina uppgifter för att få en prisuppgift"}
                                </h2>
                                <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
                                    <Close />
                                </button>
                            </div>

                            {/* Step 1 Content */}
                            {step === 1 && (
                                <div className="space-y-6">
                                    <div className="space-y-[12px] p-[20px] border border-[#E8E9EF] rounded-[16px]">
                                        {filteredItems.map((item, index) => (
                                            <div
                                                key={index}
                                                className="flex justify-between items-center border-b border-dashed  border-gray-200 pb-[12px]"
                                            >
                                                <span className="text-[20px] text-violet-300">
                                                    {item.item}
                                                </span>
                                                <span className="text-[20px] font-medium">
                                                    {item.value}
                                                </span>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="flex justify-center items-center bg-[#A4FCBC] py-[12px] rounded-[16px] uppercase">
                                        <span className="text-[20px] leading-[1.2] font-bold pr-[5px]">{lang === 'en' ? "Total space" : "totalt utrymme "} = </span>
                                        <span className="text-[20px] font-bold leading-[1.2]">
                                            {totalSpace.toFixed(1)} m³
                                        </span>
                                    </div>
                                </div>
                            )}

                            {/* Step 2 Content */}
                            {step === 2 && (
                                <div className="space-y-6">
                                    <InputField
                                        label={lang === 'en' ? "Full Name" : "Fullständigt namn"}
                                        iconLeft={<User />}
                                        placeholder={lang === 'en' ? "Write your full name" : "Skriv ditt fullständiga namn"}
                                        value={formData.fullName}
                                        onChange={(e) => setFormData({...formData, fullName: e.target.value})}
                                        errorMessage={
                                            errors.fullName ? lang === 'en' ? "This field is required" : "Detta fält är obligatoriskt" : ""
                                        }
                                    />
                                    <div className="grid grid-cols-2 gap-6 max-[576px]:gap-[16px] max-[576px]:grid-cols-1">
                                        <InputField
                                            label={lang === 'en' ? "Phone Number" : "telefonnummer"}
                                            type="number"
                                            iconLeft={<PhoneDef />}
                                            placeholder={lang === 'en' ? "Write your phone number" : "Skriv ditt telefonnummer"}
                                            value={formData.phoneNumber}
                                            onChange={(e) => setFormData({...formData, phoneNumber: e.target.value})}
                                            errorMessage={
                                                errors.phoneNumber ? lang === 'en' ? "This field is required" : "Detta fält är obligatoriskt" : ""
                                            }
                                        />
                                    
                                        <InputField
                                            label={lang === 'en' ? "E-mail" : "e-post"}
                                            iconLeft={<EmailIcon />}
                                            placeholder={lang === 'en' ? "Write your e-mail" : "Skriv din e-post"}
                                            value={formData.email}
                                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                                            errorMessage={
                                                errors.email
                                                    ? lang === 'en' ? "This field is required" : "Detta fält är obligatoriskt"
                                                    : errors.emailFormat
                                                    ? lang === 'en' ? "Please enter a valid email address" : "Ange en giltig e-postadress"
                                                    : ""
                                            }
                                        />
                                    </div>
                                    <InputField
                                        label={lang === 'en' ? "Additional Comments (Optional)" : "ytterligare kommentarer (valfritt)"}
                                        value={formData.message}
                                        type="textarea"
                                        placeholder={lang === 'en' ? "Write text" : "Skiv text"}
                                        onChange={(e) => setFormData({...formData, message: e.target.value})}
                                    />

                                    <Checkbox
                                        label={lang === 'en' ? "I consent to my personal data being processed in accordance with the" : "Jag samtycker till att mina personuppgifter behandlas i enlighet med"}
                                        required={true}
                                        checked={formData.agreed}
                                        onChange={handleCheckboxChange}
                                        link={`${lang === 'en' ? '/en' : ''}/integritetspolicy`}
                                        linktext={lang === 'en' ? "privacy policy" : "integritetspolicyn"}
                                    />
                                    {errors.agreed && (
                                        <p className="text-red-500 text-sm mt-1">{lang === 'en' ? "You must agree to continue" : "Du måste godkänna att fortsätta"}</p>
                                    )}
                                </div>
                            )}

                            {/* Footer Buttons */}
                            <div className="mt-8 flex justify-between">
                                {step === 2 ? (
                                    <Button
                                        type="secondary"
                                        text={lang === 'en' ? "Back" : "tillbaka"}
                                        onClick={handleBack}
                                        icon={<BackBtnGray />}
                                        size="big"
                                    />
                                ) : (
                                    <div></div>
                                )}

                                <Button
                                    type="primary"
                                    text={step === 1 ? lang === 'en' ? "Continue" : "Fortsätta" : lang === 'en' ? "Request a price quote" : "Fa prisoffert"}
                                    onClick={handleContinue}
                                    iconPostiton="right"
                                    size="big"
                                    icon={<ArrowRight />}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default CubeCalculatorModal;